import { Building2, ChevronDown, ChevronLeft, ChevronsUpDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useSession } from '@/features/auth';
import { UserMenu } from '@/features/auth/ui/UserMenu';
import { useOrganizationSettings } from '@/features/org';
import { Button } from '@/shared/ui/Button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';
import { ProductLogo } from '@/shared/ui/brand';
import { DEFAULT_ORGANIZATION_THEME } from '@/theme/defaults';
import { cn } from '@/shared/lib/utils';
import { APP_NAV, type NavItem } from '../../navigation/app-nav';
import { useActiveNavItem } from '../../navigation/use-active-nav';
import {
  LABEL_FADE,
  LABEL_FADE_EXPAND_DELAY,
  SIDEBAR_ICON_SLOT_CLASS,
  SIDEBAR_TRANSITION,
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED,
  SUBMENU_TRANSITION,
} from './sidebar-motion';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onOpenClinicSwitcher: () => void;
}

interface SidebarNavLabelProps {
  isCollapsed: boolean;
  children: string;
}

function SidebarNavLabel({ isCollapsed, children }: SidebarNavLabelProps): JSX.Element {
  return (
    <span className="min-w-0 flex-1 overflow-hidden whitespace-nowrap">
      <motion.span
        initial={false}
        animate={{ opacity: isCollapsed ? 0 : 1 }}
        transition={{
          ...LABEL_FADE,
          delay: isCollapsed ? 0 : LABEL_FADE_EXPAND_DELAY,
        }}
        className="block truncate"
        aria-hidden={isCollapsed}
      >
        {children}
      </motion.span>
    </span>
  );
}

function SidebarIconSlot({ children }: { children: ReactNode }): JSX.Element {
  return <span className={SIDEBAR_ICON_SLOT_CLASS}>{children}</span>;
}

interface SidebarSubmenuProps {
  item: NavItem;
  isExpanded: boolean;
  isCollapsed: boolean;
}

function SidebarSubmenu({ item, isExpanded, isCollapsed }: SidebarSubmenuProps): JSX.Element | null {
  if (!item.sub || isCollapsed) return null;

  return (
    <AnimatePresence initial={false}>
      {isExpanded ? (
        <motion.div
          key={`submenu-${item.label}`}
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={SUBMENU_TRANSITION}
          className="overflow-hidden"
        >
          <div className="mt-1 ml-5 space-y-0.5 border-l border-white/5 pl-3 pb-1">
            {item.sub.map((sub) => (
              <NavLink
                key={sub.to}
                to={sub.to}
                end={sub.to === '/'}
                className={({ isActive: subActive }) =>
                  `block rounded-md px-2.5 py-1.5 text-[12px] leading-snug transition-colors duration-150 ${subActive ? 'bg-white/5 font-medium text-white' : 'text-ink-400 hover:text-white'}`
                }
              >
                {sub.label}
              </NavLink>
            ))}
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

interface SidebarNavItemProps {
  item: NavItem;
  isCollapsed: boolean;
  isActive: boolean;
}

const collapsedFlyoutLinkClass =
  'flex w-full items-center rounded-lg px-3 py-2 text-left text-[13px] transition';

function SidebarCollapsedFlyout({
  item,
  isActive,
}: {
  item: NavItem;
  isActive: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const Icon = item.icon;
  const sub = item.sub ?? [];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={item.label}
          title={item.label}
          className={cn(
            'nav-item flex w-full items-center justify-center rounded-lg px-2 py-1 text-sm font-medium',
            isActive && 'active',
          )}
        >
          <SidebarIconSlot>
            <Icon className="size-[18px] shrink-0" aria-hidden />
          </SidebarIconSlot>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={10}
        className="w-56 p-0"
        role="menu"
      >
        <div className="flex items-center gap-2.5 border-b hairline px-3.5 py-2.5">
          <span className="flex size-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
            <Icon className="size-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-ink-900">{item.label}</div>
            <div className="text-[11px] text-ink-500">{sub.length} destinations</div>
          </div>
        </div>
        <div className="p-1.5">
          {sub.map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.to === '/'}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={({ isActive: subActive }) =>
                cn(
                  collapsedFlyoutLinkClass,
                  subActive
                    ? 'bg-brand-50 font-semibold text-brand-700'
                    : 'text-ink-700 hover:bg-surface-100',
                )
              }
            >
              {entry.label}
            </NavLink>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SidebarNavItem({ item, isCollapsed, isActive }: SidebarNavItemProps): JSX.Element {
  const Icon = item.icon;
  /** `null` = follow active section; otherwise explicit user toggle. */
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);

  useEffect(() => {
    setUserExpanded(null);
  }, [isActive]);

  const isExpanded = !isCollapsed && (userExpanded ?? isActive);
  const itemClassName = (active: boolean) =>
    `nav-item flex w-full items-center rounded-lg px-2 py-1 text-left text-sm font-medium ${active ? 'active' : ''}`;

  if (item.sub && isCollapsed) {
    return <SidebarCollapsedFlyout item={item} isActive={isActive} />;
  }

  if (!item.sub) {
    return (
      <NavLink
        to={item.to}
        end={item.to === '/'}
        title={isCollapsed ? item.label : undefined}
        className={({ isActive: linkActive }) => itemClassName(linkActive || isActive)}
      >
        <SidebarIconSlot>
          <Icon className="size-[18px]" />
        </SidebarIconSlot>
        <SidebarNavLabel isCollapsed={isCollapsed}>{item.label}</SidebarNavLabel>
      </NavLink>
    );
  }

  return (
    <div>
      <button
        type="button"
        aria-expanded={isExpanded}
        className={itemClassName(isActive)}
        onClick={() => setUserExpanded(!(userExpanded ?? isActive))}
      >
        <SidebarIconSlot>
          <Icon className="size-[18px]" />
        </SidebarIconSlot>
        <SidebarNavLabel isCollapsed={isCollapsed}>{item.label}</SidebarNavLabel>
        <motion.span
          initial={false}
          animate={{ opacity: isCollapsed ? 0 : 1 }}
          transition={LABEL_FADE}
          className="ml-auto flex w-4 shrink-0 items-center justify-center overflow-hidden"
          aria-hidden={isCollapsed}
        >
          <motion.span
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={SUBMENU_TRANSITION}
            className="inline-flex"
          >
            <ChevronDown className="size-4 opacity-70" />
          </motion.span>
        </motion.span>
      </button>

      <SidebarSubmenu item={item} isExpanded={isExpanded} isCollapsed={isCollapsed} />
    </div>
  );
}

/**
 * App navigation sidebar with org branding, clinic switcher, and user footer.
 */
export function Sidebar({
  isCollapsed,
  onToggle,
  onOpenClinicSwitcher,
}: SidebarProps): JSX.Element {
  const activeItem = useActiveNavItem();
  const { activeClinic } = useSession();
  const { data: orgSettings } = useOrganizationSettings();
  const appBrandName = DEFAULT_ORGANIZATION_THEME.clinicName;
  const organizationName = orgSettings?.displayName?.trim() ?? '';
  const currentClinicName = activeClinic?.clinicName ?? 'Select clinic';

  return (
    <motion.div
      initial={false}
      animate={{ width: isCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED }}
      transition={SIDEBAR_TRANSITION}
      className={cn(
        'relative hidden h-screen flex-none flex-col bg-ink-900 text-ink-400 lg:flex',
        isCollapsed && 'is-collapsed',
      )}
    >
      <div
        className={cn(
          'relative z-10 flex shrink-0 flex-col border-b border-white/[0.06]',
          isCollapsed ? 'items-center gap-2.5 px-2 py-3.5' : 'gap-2.5 px-3 py-3.5',
        )}
      >
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.05] via-white/[0.02] to-transparent"
          aria-hidden
        />

        <Link
          to="/"
          title={appBrandName}
          className={cn(
            'group relative flex min-w-0 items-center rounded-xl transition-colors duration-200',
            isCollapsed ? 'justify-center p-1.5' : 'gap-3 px-1.5 py-1 hover:bg-white/[0.04]',
          )}
        >
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/[0.06] ring-1 ring-white/10 transition duration-200 group-hover:bg-white/[0.08] group-hover:ring-white/15">
            <ProductLogo onDark size="sm" className="size-6" alt={`${appBrandName} logo`} />
          </span>
          {!isCollapsed ? (
            <motion.div
              initial={false}
              animate={{ opacity: 1 }}
              transition={{ ...LABEL_FADE, delay: LABEL_FADE_EXPAND_DELAY }}
              className="min-w-0 flex-1 overflow-hidden leading-tight"
            >
              <div className="truncate font-display text-[15px] font-semibold tracking-tight text-white">
                {appBrandName}
              </div>
              {organizationName ? (
                <div className="truncate text-xs font-medium text-ink-400">
                  {organizationName}
                </div>
              ) : null}
            </motion.div>
          ) : null}
        </Link>

        {isCollapsed ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-10 rounded-xl border border-white/[0.08] bg-white/[0.03] text-ink-400 hover:border-white/12 hover:bg-white/[0.06] hover:text-white"
            onClick={onOpenClinicSwitcher}
            aria-label={`Switch clinic (current: ${currentClinicName})`}
            title={currentClinicName}
          >
            <Building2 className="size-4" aria-hidden />
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            onClick={onOpenClinicSwitcher}
            className="h-auto w-full justify-start gap-2.5 rounded-xl border border-white/[0.08] bg-white/[0.03] px-2.5 py-2.5 text-left text-ink-300 transition duration-200 hover:border-white/12 hover:bg-white/[0.06] hover:text-white"
            aria-label="Switch clinic"
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-500/15 text-brand-300 ring-1 ring-brand-400/20">
              <Building2 className="size-3.5" aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-500">
                Workspace
              </span>
              <span className="block truncate text-[13px] font-semibold leading-snug text-white">
                {currentClinicName}
              </span>
            </span>
            <ChevronsUpDown className="size-3.5 shrink-0 text-ink-500" aria-hidden />
          </Button>
        )}

        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onToggle}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!isCollapsed}
          className={cn(
            'group absolute -bottom-3 -right-3 z-40 size-6 rounded-full border-surface-200/90 bg-white p-0 text-ink-500 shadow-soft',
            'hover:border-brand-200 hover:bg-brand-50 hover:text-brand-700 hover:shadow-card',
            'focus-visible:ring-brand-400/40 active:scale-95',
          )}
        >
          <motion.span
            initial={false}
            animate={{ rotate: isCollapsed ? 180 : 0 }}
            transition={SIDEBAR_TRANSITION}
            className="inline-flex"
          >
            <ChevronLeft
              className="size-3.5 transition-transform duration-200 group-hover:scale-110"
              aria-hidden
            />
          </motion.span>
        </Button>
      </div>

      <nav className="min-h-0 flex-1 space-y-px overflow-y-auto overflow-x-hidden nice-scroll px-2 pb-3 pt-3">
        {APP_NAV.map((group) => (
          <div key={group.section}>
            {!isCollapsed ? (
              <div
                className={`nav-section-label mb-1 overflow-hidden pl-2 text-[10px] font-semibold uppercase tracking-widest text-ink-500 ${group.section === 'Main' ? '' : 'mt-2.5'}`}
              >
                {group.section}
              </div>
            ) : null}

            {group.items.map((item) => (
              <SidebarNavItem
                key={item.label}
                item={item}
                isCollapsed={isCollapsed}
                isActive={activeItem === item}
              />
            ))}
          </div>
        ))}
      </nav>

      <div className="shrink-0 border-t border-white/5 p-2">
        <UserMenu variant="sidebar" isCollapsed={isCollapsed} />
      </div>
    </motion.div>
  );
}
